import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { environment } from '../../../environments/environment';
import { isAdminOrSuperUser, isSuperUser } from '../utils/RoleUtils';

/**
 * Guard que protege rutas verificando si el usuario tiene los permisos necesarios.
 *
 * Funciona de dos maneras:
 * 1. Si el usuario es admin/super user con permisos personalizados (environment.userPermissions),
 *    verifica si tiene acceso a la ruta específica según los permisos del backend.
 * 2. Si el usuario no tiene permisos personalizados, verifica los roles tradicionales.
 *
 * Uso en rutas:
 * {
 *   path: 'pantalla-cliente',
 *   component: CustomerDisplayAdminComponent,
 *   canActivate: [permissionGuard],
 *   data: { requiredPath: '/pantalla-cliente', requiredRole: 'SUPER_USER' }
 * }
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);

  // Verificar si el usuario está autenticado
  if (!environment.token) {
    console.log('[PermissionGuard] User not authenticated, redirecting to login');
    router.navigate(['/login']);
    return false;
  }

  // Obtener el path requerido de la metadata de la ruta
  const requiredPath = route.data['requiredPath'] as string;
  const requiredRole = route.data['requiredRole'] as string;

  // Si no hay requisitos de permisos especiales, permitir acceso
  if (!requiredPath && !requiredRole) {
    return true;
  }

  // Si el usuario tiene permisos personalizados (admin/super user), verificar en los permisos
  if (environment.userPermissions && environment.userPermissions.modules) {
    console.log('[PermissionGuard] Checking custom permissions for path:', requiredPath);

    // Buscar si el usuario tiene permiso para acceder a esta ruta
    let hasPermission = environment.userPermissions.modules.some((module: any) =>
      module.options.some((option: any) => option.path === requiredPath && option.allow === true)
    );

    // Hasta que el backend envíe /ordenes-compra, quien puede envíos puede órdenes de compra
    if (!hasPermission && requiredPath === '/ordenes-compra') {
      hasPermission = environment.userPermissions.modules.some((module: any) =>
        module.options.some(
          (option: any) => option.path === '/listar-envios' && option.allow === true
        )
      );
    }

    if (hasPermission) {
      console.log('[PermissionGuard] Access granted via custom permissions');
      return true;
    } else {
      console.log('[PermissionGuard] Access denied - no permission for this route');
      router.navigate(['/nueva-venta']);
      return false;
    }
  }

  // Si no tiene permisos personalizados, verificar por roles
  // Primero verificar si la ruta requiere un rol específico
  if (requiredRole) {
    if (requiredRole === 'SUPER_USER') {
      if (isSuperUser()) {
        console.log('[PermissionGuard] Access granted via SUPER_USER role');
        return true;
      } else {
        console.log('[PermissionGuard] Access denied - SUPER_USER role required');
        router.navigate(['/nueva-venta']);
        return false;
      }
    }

    if (requiredRole === 'ADMIN' || requiredRole === 'ADMIN_OR_SUPER_USER') {
      if (isAdminOrSuperUser()) {
        console.log('[PermissionGuard] Access granted via ADMIN role');
        return true;
      } else {
        console.log('[PermissionGuard] Access denied - ADMIN role required');
        router.navigate(['/nueva-venta']);
        return false;
      }
    }
  }

  // Si no tiene permisos personalizados y no requiere rol específico,
  // permitir acceso (rutas públicas para usuarios autenticados)
  console.log('[PermissionGuard] Access granted - no specific permission required');
  return true;
};
